// @ts-strict-ignore
import type { SnippetVersion, SnippetDetails } from 'flow/service/snippets/types';
import type { AppDispatch, State } from '~/store';
import {
  appendPrimitiveToCollisionMap,
  buildPrimitives,
  getCollisionsWithinPrimitives,
  getSnippetActionsWithNonResolvableCollisions,
} from 'flow/service/snippets/collisions';
import { getEquivalencyKeyForAction, getEquivalencyKeyForPrimitive, getEquivalencyKeyForActionRules } from 'flow/service/snippets/collisions/equivalency';

import type {
  Action,
  DataRoute,
  PublicId,
  ReasonCodeGroup,
  Threshold,
} from 'flow/store/engine/types';
import {
  selectActionByPublicId,
  selectOutcomes,
  selectPreSnippetWorkflowDataSnapshotId,
  selectPublishedAttributes,
  selectReasonCodeGroups,
  selectReasonCodes,
  selectServices,
  selectTags,
  selectWorkflowData,
  selectWorkflowDataSnapshotById,
  selectDefaultPrecedenceIndexedAction,
  selectNodeFolderState,
  selectActions,
  selectPreSnippetWorkflowPropsSnapshot,
  selectWorkflowIsSnippet,
  selectLists,
  selectLocalSnippetData,
  selectThresholds,
  selectDataRoutes,
  selectExternalWorkflowActions,
} from '../selectors';
import {
  actionHasDataRoutes,
  actionHasRules,
  getPublishedDetails,
  includesActionOfType,
  isActionOfType,
  isDefaultPrecedenceIndexedAction,
} from 'flow/service/actions';
import {
  addThreshold,
  updateAction,
  addAction,
  addEphemeralTagToProps,
  addEphemeralOutcomeToProps,
  addEphemeralPublishedAttributeToProps,
  addEphemeralAttributeToProps,
  setSnippetMode,
  setSnippetActiveOperation,
  setPreSnippetWorkflowDataSnapshotId,
  updateWorkflowSnapshots,
  addEphemeralReasonCodeGroupToProps,
  applyWorkflowDataSnapshot,
  removeSubsequentWorkflowDataSnapshots,
  setSnippetPreviewData,
  setNodeFolderCollapsed,
  setPreSnippetWorkflowPropsSnapshot,
  applyWorkflowPropsSnapshot,
  updateDependencies,
  setSnippetIncompatibleActions,
  addEphemeralListToProps,
  setSnippetCollapsed,
  replaceWorkflowDataGraph,
  setFocusedNodeId,
} from '../slice';
import {
  contextualizeSnippetForTargetWorkflow,
  sortSnippetInTopologicalOrder,
  isValidActionMerge,
  mergeActionRules,
  resolveDataRouteSourceReferences,
  resolveThresholdParentReferences,
  pruneSnippetVersion,
  getIncompatibleActions,
  isActionSnippetCompatible,
  prepareLinkedSnippetForInsertion,
  regenerateLinkedSnippetsPublicIds,
  buildLocalNextSnippet,
  applySnippetVersionUpgrade,
} from 'flow/service/snippets/snippets';
import {
  getAllThresholdFromRules, indexThresholdsByPublicId,
} from 'flow/service/thresholds';
import { buildRuleState } from 'flow/service/rules';
import { getDataRoutesForActions } from 'flow/service/dataRoutes';
import { ActionType } from 'flow/store/engine/constants';
import { indexTagsByName } from 'flow/service/tags';
import { indexOutcomesByName } from 'flow/service/outcomes';
import { indexPublishedAttributesByName, isPublishedAttribute } from 'flow/service/publishedAttributes';
import { snippetMode, snippetOperation } from '../types';
import { snapshotAndCloneWorkflowData } from '../helpers';
import { buildDependencies } from '../../engine/dependencies/dependencies';
import { exitLinkedChildActionThunk, replaceDefaultPrecedenceIndexedAction } from './orchestration';
import { createSnapshot } from 'flow/service/history';
import { batch } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { yieldToMain } from 'flow/service/util';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import type { PreparedSnippetInfo } from './types';
import { indexServicesByInternalName } from 'flow/service/services';
import {
  getAttributesForServices,
  getUsedAttributeTokens,
  indexServiceInternalNamesByAttributeToken,
} from 'flow/service/services/attributes';
import { isListAction } from 'flow/service/actions/services/lists';
import { indexListsByName } from 'flow/service/lists';
import { generatePublicId } from 'flow/store/engine/utils';
import { appendSnippetParent, isExternalSnippetChildAction } from 'flow/service/actions/snippets';
import type { SnippetScope } from 'flow/editor/components/MenuBar/MenuBarModals/PolicyLibraryModal/constants';


function preparePreInsertSnapshotThunk() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const { workflowEditor: state } = getState();

    const { workflowSnapshots } = snapshotAndCloneWorkflowData(state);
    dispatch(updateWorkflowSnapshots({ workflowSnapshots }));

    const preInsertWorkflowDataSnapshot = workflowSnapshots.workflowData.history.past[
      workflowSnapshots.workflowData.history.past.length - 1
    ];
    const preInsertWorkflowPropsSnapshot = createSnapshot({
      data: structuredClone(state.workflowProps),
    });

    dispatch(setPreSnippetWorkflowDataSnapshotId({
      snapshotId: preInsertWorkflowDataSnapshot.snapshotId,
    }));
    dispatch(setPreSnippetWorkflowPropsSnapshot({
      snapshot: preInsertWorkflowPropsSnapshot,
    }));
  };
}

/**
 * Inserts a snippet version into the workflow with loading state and all other
 * UI requirements.
 * This is an orchestration wrapper that automatically sets the pastePending mode,
 * yields to the event loop for React to render the spinner, then runs the insertion, and should
 * be used when invoking this functionality from the UI.
 */
export function insertSnippetVersionThunk(args: {
  snippet: SnippetVersion;
}) {
  return async (dispatch: AppDispatch, getState: () => State) => {
    const { snippet } = args;
    try {
      // state and UI updates before running the blocking operation
      dispatch(setSnippetMode({ mode: snippetMode.pastePending }));
      dispatch(exitLinkedChildActionThunk());

      // yield control to main thread before running the blocking operation to render the spinner
      await yieldToMain();

      dispatch(preparePreInsertSnapshotThunk());

      // run core operation (blocking)
      dispatch(processSnippetVersionInsertionThunk({ snippet }));

      // handle post merge ui changes below
      dispatch(setSnippetActiveOperation({
        operation: snippetOperation.insertSnippet,
        snippetActionPublicId: null,
      }));
      dispatch(setSnippetMode({ mode: snippetMode.review }));

      // open node folder if it has a child that was impacted by merge
      const { isCollapsed } = selectNodeFolderState(getState());
      const hasInputAttr = includesActionOfType({
        type: ActionType.INPUT,
        actions: Object.values(snippet.data.actions),
      });

      if (hasInputAttr && isCollapsed) {
        dispatch(setNodeFolderCollapsed({ isCollapsed: false }));
      }
    } catch (error) {
      dispatch(undoInsertSnippetVersionThunk());
      error.name = 'InsertSnippetError';
      datadogRum.addError(error, {
        operation: 'insert_snippet',
        url: window.location.pathname,
      });
      captureExceptionsToSentry(error);
      throw error;
    }
  };
}


/**
 * Inserts a snippet version into the workflow.
 * This thunk contains the core logic for the insertion algorithm, and any pre and post
 * UI and state work leading to/from it should be handled in `insertSnippetVersionThunk` instead.
 *
 * # **This thunk should **NOT** be called from the UI directly.**
 */
export function processSnippetVersionInsertionThunk(args: {
  snippet: SnippetVersion;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const initialState = getState();

    // pre-process the Snippet
    const { snippet, incompatibleActions } = prepareSnippetForInsertion({
      state: initialState,
      snippet: args.snippet,
    });

    const snippetTagsByName = indexTagsByName(snippet.props.tags);
    const snippetOutcomesByName = indexOutcomesByName(snippet.props.outcomes);
    const snippetPublishedAttributesByName = indexPublishedAttributesByName(
      snippet.props.publishedAttributes,
    );
    const snippetServicesByInternalName = indexServicesByInternalName(snippet.props.services);
    const snippetListsByName = indexListsByName(snippet.props.lists);

    // We only need to add ephemeral attributes to props if they're actually in the snippet
    const usedAttributeTokens = getUsedAttributeTokens({
      actions: snippet.data.actions,
      thresholds: snippet.data.thresholds,
      dataRoutes: snippet.data.dataRoutes,
    });
    const snippetAttributes = getAttributesForServices(snippet.props.services, {
      includeOnly: usedAttributeTokens,
    });
    const snippetServiceInternalNameByAttrToken = indexServiceInternalNamesByAttributeToken(
      snippet.props.services,
    );

    const initialWorkflowData = selectWorkflowData(initialState);
    const initialAccountTags = selectTags(initialState);
    const initialAccountOutcomes = selectOutcomes(initialState);
    const initialAccountPublishedAttributes = selectPublishedAttributes(initialState);
    const initialAccountReasonCodeGroups = selectReasonCodeGroups(initialState);
    const initialAccountServices = selectServices(initialState);
    const initialAccountLists = selectLists(initialState);
    const workflowHasInitialDefaultAction = Boolean(
      selectDefaultPrecedenceIndexedAction(initialState),
    );

    /**
       * we build an initial collision map for the workflow primitives, and the insertion
       * algorithm will add new entries as it adds new ephemeral props.
       * action entries should not be added, as we need the initial action entries to avoid
       * merging duplicate snippet actions within each other.
       * if an up-to-date map with actions entries is needed, track those entries in a separate map
       */
    const workflowCollisionMap = getCollisionsWithinPrimitives({
      primitives: buildPrimitives({
        actions: initialWorkflowData.actions,
        services: initialAccountServices,
        tags: initialAccountTags,
        outcomes: initialAccountOutcomes,
        publishedAttributes: initialAccountPublishedAttributes,
        reasonCodeGroups: initialAccountReasonCodeGroups,
        lists: initialAccountLists,
      }),
      context: {
        workflowData: {
          dataRoutes: initialWorkflowData.dataRoutes,
          services: initialAccountServices,
        },
      },
    });

    // maps the id of a snippet action to the id of a merged wf action, if any
    const mergedActionMap = new Map<Action['publicId'], Action['publicId']>();

    // Batch all UI updates to re-render only once
    batch(() => {
      if (incompatibleActions.length) {
        dispatch(setSnippetIncompatibleActions({ actions: incompatibleActions }));
      }

      // Process ephemeral attributes from snippet services
      for (const snippetAttribute of snippetAttributes) {
        if (!snippetAttribute.isEphemeral) {
          /* eslint-disable-next-line no-continue */
          continue;
        }
        const serviceInternalName = snippetServiceInternalNameByAttrToken[
          snippetAttribute.attribute_token
        ];
        if (serviceInternalName == null) {
          throw new Error('No service found for ephemeral attribute');
        }

        const attributeEquivalencyKey = getEquivalencyKeyForPrimitive({
          primitive: {
            attribute: snippetAttribute,
            serviceInternalName,
          },
        });

        const hasAttributeCollision = workflowCollisionMap.has(attributeEquivalencyKey);

        if (!hasAttributeCollision) {
          appendPrimitiveToCollisionMap({
            collisionMap: workflowCollisionMap,
            equivalencyKey: attributeEquivalencyKey,
            primitive: {
              attribute: snippetAttribute,
              serviceInternalName,
            },
          });

          dispatch(addEphemeralAttributeToProps({
            attribute: snippetAttribute,
            serviceInternalName,
          }));
        }
      }

      // todo BUILD-3766 - support other all primitives besides Actions
      for (const snippetAction of Object.values(snippet.data.actions)) {
        const currentState = getState();
        const currentWorkflowData = selectWorkflowData(currentState);

        /**
         * Computes the equivalency key for the snippet action as it is evaluated.
         * This cannot be computed upfront, as we need the up-to-date merged action map.
         */
        const snippetActionEquivalencyKey = getEquivalencyKeyForAction({
          action: snippetAction,
          context: {
            actionMergeMap: mergedActionMap,
            workflowData: {
              dataRoutes: snippet.data.dataRoutes,
              services: snippetServicesByInternalName,
            },
          },
        });

        // ===== artifacts
        if (isActionOfType(snippetAction, ActionType.TAG)) {
          const snippetTagRelatedToSnippetAction = snippetTagsByName[snippetAction.tag.name];

          const tagEquivalenceKey = getEquivalencyKeyForPrimitive({
            primitive: snippetTagRelatedToSnippetAction,
          });
          const hasTagCollision = workflowCollisionMap.has(tagEquivalenceKey);

          if (!hasTagCollision) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: tagEquivalenceKey,
              primitive: snippetTagRelatedToSnippetAction,
            });

            dispatch(addEphemeralTagToProps({
              name: snippetTagRelatedToSnippetAction.tag_name,
              color: snippetTagRelatedToSnippetAction.tag_color,
              description: snippetTagRelatedToSnippetAction.description,
              isAdverseActionReason: snippetTagRelatedToSnippetAction.is_adverse_action_reason,
              isCaseCreating: snippetTagRelatedToSnippetAction.is_case_creating,
              suppressionInterval: snippetTagRelatedToSnippetAction.suppression_interval,
            }));
          }
        }

        if (isActionOfType(snippetAction, ActionType.OUTCOME)) {
          const snippetOutcomeRelatedToSnippetAction = (
            snippetOutcomesByName[snippetAction.outcome.name]
          );

          const outcomeEquivalencyKey = getEquivalencyKeyForPrimitive({
            primitive: snippetOutcomeRelatedToSnippetAction,
          });

          const hasOutcomeCollision = workflowCollisionMap.has(outcomeEquivalencyKey);

          if (!hasOutcomeCollision) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: outcomeEquivalencyKey,
              primitive: snippetOutcomeRelatedToSnippetAction,
            });

            dispatch(addEphemeralOutcomeToProps({
              name: snippetOutcomeRelatedToSnippetAction.outcome,
              color: snippetOutcomeRelatedToSnippetAction.outcome_color,
              isCaseStatus: snippetOutcomeRelatedToSnippetAction.is_case_status,
              isEventStatus: snippetOutcomeRelatedToSnippetAction.is_event_status,
            }));
          }
        }

        if (isPublishedAttribute(snippetAction)) {
          const snippetAttributeActionName = getPublishedDetails(snippetAction)?.attributeName;
          const snippetPublishedAttributeRelatedToAction = (
            snippetPublishedAttributesByName[snippetAttributeActionName]
          );

          const publishedAttributeEquivalencyKey = getEquivalencyKeyForPrimitive({
            primitive: snippetPublishedAttributeRelatedToAction,
          });

          const hasPublishedAttributeCollision = (
            workflowCollisionMap.has(publishedAttributeEquivalencyKey)
          );

          if (!hasPublishedAttributeCollision && snippetPublishedAttributeRelatedToAction) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: publishedAttributeEquivalencyKey,
              primitive: snippetPublishedAttributeRelatedToAction,
            });

            dispatch(addEphemeralPublishedAttributeToProps({
              attributeData: {
                ...snippetPublishedAttributeRelatedToAction,
              },
            }));
          }
        }

        if (isListAction(snippetAction)) {
          const snippetListRelatedToSnippetAction = snippetListsByName[snippetAction.service.name];

          const listEquivalencyKey = getEquivalencyKeyForPrimitive({
            primitive: snippetListRelatedToSnippetAction,
          });

          const hasListCollision = workflowCollisionMap.has(listEquivalencyKey);

          if (!hasListCollision && snippetListRelatedToSnippetAction) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: listEquivalencyKey,
              primitive: snippetListRelatedToSnippetAction,
            });

            dispatch(addEphemeralListToProps({ list: snippetListRelatedToSnippetAction }));
          }
        }

        const hasEphemeralReasonCodeGroups = actionHasThresholdsWithEphemeralRCGs({
          action: snippetAction,
          thresholds: snippet.data.thresholds,
        });

        if (hasEphemeralReasonCodeGroups) {
          const ephemeralRCGForActionThresholds = getEphemeralReasonCodeGroupsFromActionThresholds({
            action: snippetAction,
            thresholds: snippet.data.thresholds,
          });

          for (const ephemeralRCG of ephemeralRCGForActionThresholds) {
            const ephemeralRCGEquivalencyKey = getEquivalencyKeyForPrimitive({
              primitive: ephemeralRCG,
            });

            const hasRCGCollision = workflowCollisionMap.has(ephemeralRCGEquivalencyKey);

            if (!hasRCGCollision) {
              appendPrimitiveToCollisionMap({
                collisionMap: workflowCollisionMap,
                equivalencyKey: ephemeralRCGEquivalencyKey,
                primitive: ephemeralRCG,
              });

              dispatch(addEphemeralReasonCodeGroupToProps({ reasonCodeGroup: ephemeralRCG }));
            }
          }
        }


        const collisionSetForSnippetAction = workflowCollisionMap.get(snippetActionEquivalencyKey);
        const hasActionCollision = (collisionSetForSnippetAction?.size ?? 0) > 0;

        if (!hasActionCollision) {
          /**
           * if evaluating a default indexed action (outcomes and workflow routing actions) and the
           * workflow already has one, the workflow's gets replaced with the snippet's.
           * this is the only different branch in logic for adding non-colliding actions, and should
           * run at most once, as there is at most one default action in a snippet.
           *
           * a snippet default action on a workflow that has none is added via the else branch below
           */
          if (workflowHasInitialDefaultAction
            && isDefaultPrecedenceIndexedAction(snippetAction)) {
            dispatch(replaceDefaultPrecedenceIndexedAction(snippetAction, {
              publicId: snippetAction.publicId,
            }));
          } else {
            let thresholdsToAdd: Threshold[] | undefined;
            if (actionHasRules(snippetAction)) {
              thresholdsToAdd = resolveThresholdsForAction({
                action: snippetAction,
                thresholds: snippet.data.thresholds,
                actionIdMap: mergedActionMap,
              });
            }

            let dataRoutesToAdd: DataRoute[] | undefined;
            if (actionHasDataRoutes(snippetAction)) {
              dataRoutesToAdd = resolveDataRoutesForAction({
                action: snippetAction,
                dataRoutes: snippet.data.dataRoutes,
                actionIdMap: mergedActionMap,
              });
            }

            dispatch(addAction({
              action: snippetAction,
              thresholds: thresholdsToAdd,
              dataRoutes: dataRoutesToAdd,
            }));
          }
        } else {
          /**
           * grab the first-appearing colliding action.
           * further work can be done to manually pick which action to merge with.
           * - if picking a specific action, pass it into the thunk as a param in the payload.
          */
          const collidingActionPublicIds = [...collisionSetForSnippetAction];
          const firstAppearingCollidingActionPublicId = collidingActionPublicIds.at(0);

          const workflowActionToMergeInto = (
            selectActionByPublicId(firstAppearingCollidingActionPublicId)
          )(currentState);

          const mergeIsValid = isValidActionMerge({
            snippet: {
              actions: snippet.data.actions,
              thresholds: snippet.data.thresholds,
              dataRoutes: snippet.data.dataRoutes,
              actionToMerge: snippetAction,
            },
            workflow: {
              actions: currentWorkflowData.actions,
              thresholds: currentWorkflowData.thresholds,
              dataRoutes: currentWorkflowData.dataRoutes,
              actionToMergeInto: workflowActionToMergeInto,
            },
            actionMergeMap: mergedActionMap,
          });


          if (mergeIsValid) {
            mergedActionMap.set(snippetAction.publicId, firstAppearingCollidingActionPublicId);

            if (actionHasRules(snippetAction)) {
              const resolvedSnippetActionThresholds = resolveThresholdsForAction({
                action: snippetAction,
                thresholds: snippet.data.thresholds,
                actionIdMap: mergedActionMap,
              });

              const snActionRulesEquivalencyKey = getEquivalencyKeyForActionRules({
                rules: snippetAction.rules,
                thresholds: indexThresholdsByPublicId(resolvedSnippetActionThresholds),
              });

              const wfActionRulesToMergeIntoEqKey = getEquivalencyKeyForActionRules({
                rules: workflowActionToMergeInto.rules,
                thresholds: currentWorkflowData.thresholds,
              });

              /**
               * when merging an Action, we want to avoid adding rules if they are equivalent to the
               * merged action's. this mostly happens when adding a snippet that has a lot of
               * overlap with the target workflow.
               */
              const hasRulesCollision = (
                snActionRulesEquivalencyKey === wfActionRulesToMergeIntoEqKey
              );
              if (!hasRulesCollision) {
                for (const snippetThreshold of resolvedSnippetActionThresholds) {
                  dispatch(addThreshold({ threshold: snippetThreshold }));
                }
                const mergedRuleTree = mergeActionRules({
                  snippetAction,
                  workflowAction: workflowActionToMergeInto,
                });

                const ruleStateToInsert = buildRuleState(mergedRuleTree);
                dispatch(updateAction({
                  path: `${workflowActionToMergeInto.publicId}.rules`,
                  value: ruleStateToInsert,
                  // skip auto snapshots & dependency compilation to improve performance
                  skipSnapshotAndDependencies: true,
                }));
              }
            }

            /* No need to resolve Data Routes here, since they are not merged like Rules are. */
          } else {
            let thresholdsToAdd: Threshold[] | undefined;
            if (actionHasRules(snippetAction)) {
              thresholdsToAdd = resolveThresholdsForAction({
                action: snippetAction,
                actionIdMap: mergedActionMap,
                thresholds: snippet.data.thresholds,
              });
            }

            /**
           * Data Routes do not need to be resolved here because the collision detection
           * algorithm makes it impossible for Outputs to create graph cycles, and Matrices
           * can only create cycles via their Rules, a scenario that is handled by resolving
           * their Thresholds
           */
            dispatch(addAction({
              action: snippetAction,
              thresholds: thresholdsToAdd,
            }));
          }
        }
      }

      // dependencies should be updated manually at the end since we opted out
      dispatch(updateDependencies());
    });
  };
}

/**
 * Prepares a Snippet for insertion into the target account.
 * This is a normalization step that runs a series of operations on a Snippet
 * to simplify the downstream insertion algorithm. Returns the prepared snippet, and a list
 * of incompatible actions that were pruned from the snippet.
 *
 * Operations:
 * - **Prune Actions**: prunes incompatible actions out of the pasted snippet, if applicable.
 * - **Contextualization**: updates the names of the snippet primitives (and the corresponding actions),
 *                          that have collisions with the target account, as well as flags the props and
 *                          actions that are ephemeral, eliminating the need to run checks and updates
 *                          during insertion.
 * - **Topological Sort**: sorts the actions within the snippet in a topologically-valid order,
 *                         ensuring that every parent action is evaluated before any of its children.
 */
function prepareSnippetForInsertion(args: {
  snippet: SnippetVersion;
  state: State;
}): PreparedSnippetInfo {
  const { snippet, state } = args;

  const accountTags = selectTags(state);
  const accountOutcomes = selectOutcomes(state);
  const accountPublishedAttributes = selectPublishedAttributes(state);
  const accountReasonCodeGroups = selectReasonCodeGroups(state);
  const accountServices = selectServices(state);
  const accountReasonCodes = selectReasonCodes(state);
  const accountLists = selectLists(state);
  const workflowActions = selectActions(state);
  const isSnippetWorkflow = selectWorkflowIsSnippet(state);

  const incompatibleActions = isSnippetWorkflow
    ? getIncompatibleActions({ snippet, test: isActionSnippetCompatible })
    : [];

  const prunedSnippet = pruneSnippetVersion({
    snippet,
    actionsToPrune: incompatibleActions,
  });

  const contextualizedSnippet = contextualizeSnippetForTargetWorkflow({
    snippet: prunedSnippet,
    accountPrimitives: {
      outcomes: accountOutcomes,
      publishedAttributes: accountPublishedAttributes,
      reasonCodeGroups: accountReasonCodeGroups,
      reasonCodes: accountReasonCodes,
      services: accountServices,
      tags: accountTags,
      lists: accountLists,
    },
    workflowPrimitives: {
      actions: workflowActions,
    },
  });

  const sortedSnippet = sortSnippetInTopologicalOrder({ snippet: contextualizedSnippet });

  return {
    snippet: sortedSnippet,
    incompatibleActions,
  };
}


function resolveThresholdsForAction(args: {
  action: Action;
  thresholds: Record<string, Threshold>;
  actionIdMap: Map<Action['publicId'], Action['publicId']>;
}) {
  const {
    action,
    actionIdMap,
    thresholds,
  } = args;

  const actionThresholds = getAllThresholdFromRules({
    rules: action.rules,
    thresholds,
  });

  return resolveThresholdParentReferences({
    actionIdMap,
    thresholds: actionThresholds,
  });
}

function resolveDataRoutesForAction(args: {
  action: Action;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  actionIdMap: Map<Action['publicId'], Action['publicId']>;
}) {
  const { action, dataRoutes, actionIdMap } = args;


  const actionDataRoutes = getDataRoutesForActions({
    action,
    dataRoutes,
  });

  return resolveDataRouteSourceReferences({
    actionIdMap,
    dataRoutes: actionDataRoutes,
  });
}

export function resetSnippetUiThunk() {
  return (dispatch: AppDispatch) => {
    dispatch(setSnippetMode({ mode: null }));
    dispatch(setSnippetActiveOperation({ operation: null, snippetActionPublicId: null }));
    dispatch(setPreSnippetWorkflowDataSnapshotId({ snapshotId: null }));
    dispatch(setPreSnippetWorkflowPropsSnapshot({ snapshot: null }));
    dispatch(setSnippetIncompatibleActions({ actions: [] }));
    dispatch(setFocusedNodeId(null));
  };
}

function actionHasThresholdsWithEphemeralRCGs(args: {
  action: Action;
  thresholds: Record<Threshold['publicId'], Threshold>;
}): boolean {
  const { action, thresholds } = args;
  if (!actionHasRules(action)) {
    return false;
  }

  const actionThresholds = getAllThresholdFromRules({
    rules: action.rules,
    thresholds,
  });

  return actionThresholds.some(threshold => Boolean(threshold.right?.reasonCodeGroup));
}


function getEphemeralReasonCodeGroupsFromActionThresholds(args: {
  action: Action;
  thresholds: Record<Threshold['publicId'], Threshold>;
}): ReasonCodeGroup[] {
  const { action, thresholds } = args;

  if (!actionHasRules(action)) {
    return [];
  }

  const actionThresholds = getAllThresholdFromRules({
    rules: action.rules,
    thresholds,
  });

  const uniqueReasonCodeGroups = new Map<ReasonCodeGroup['name'], ReasonCodeGroup>();

  actionThresholds.forEach(threshold => {
    const thresholdRCG = threshold.right?.reasonCodeGroup;
    if (thresholdRCG?.isEphemeral) {
      uniqueReasonCodeGroups.set(thresholdRCG.name, thresholdRCG);
    }
  });

  return Array.from(uniqueReasonCodeGroups.values());
}

/**
 * Displays the snippet preview and updates the workflow snapshots.
 */
export function viewSnippetPreviewThunk(args: {
  snippetDetails: SnippetDetails;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const { snippet_data: snippetData, snippet_metadata: snippetMetadata } = args.snippetDetails;
    const state = getState();

    // Update workflow snapshots with current state (creates and adds snapshot to past history)
    const preSnippetWorkflowProps = structuredClone(state.workflowEditor.workflowProps);
    const { workflowSnapshots } = snapshotAndCloneWorkflowData(state.workflowEditor);
    dispatch(updateWorkflowSnapshots({ workflowSnapshots }));

    // Generate dependencies
    const snippetDependencies = buildDependencies({
      actions: snippetData.actions,
      thresholds: snippetData.thresholds,
      dataRoutes: snippetData.dataRoutes,
    });

    // Update workflow data with snippet data
    dispatch(setSnippetPreviewData({
      actions: snippetData.actions,
      thresholds: snippetData.thresholds,
      dataRoutes: snippetData.dataRoutes,
      dependencies: snippetDependencies,
      workflowToken: snippetData.token,
      name: snippetMetadata.name,
      publicVersion: snippetMetadata.published_version,
    }));

    // Get the snapshot ID for the original workflow state
    const workflowPropsSnapshot = createSnapshot({ data: preSnippetWorkflowProps });
    const pastHistory = workflowSnapshots.workflowData.history.past;
    const currentSnapshot = pastHistory[pastHistory.length - 1];
    dispatch(setSnippetMode({ mode: snippetMode.preview }));
    dispatch(setPreSnippetWorkflowDataSnapshotId({ snapshotId: currentSnapshot.snapshotId }));
    dispatch(setPreSnippetWorkflowPropsSnapshot({ snapshot: workflowPropsSnapshot }));
  };
}

export function undoInsertSnippetVersionThunk() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();

    const snapshotId = selectPreSnippetWorkflowDataSnapshotId(state);
    const workflowDataSnapshot = selectWorkflowDataSnapshotById(snapshotId)(state);
    const workflowPropsSnapshot = selectPreSnippetWorkflowPropsSnapshot(state);

    if (!workflowDataSnapshot || !workflowPropsSnapshot) {
      return;
    }

    dispatch(applyWorkflowDataSnapshot({ snapshot: workflowDataSnapshot }));
    dispatch(applyWorkflowPropsSnapshot({ snapshot: workflowPropsSnapshot }));

    // remove all snapshots relating to the snippet action
    dispatch(removeSubsequentWorkflowDataSnapshots({ snapshotId }));
    // update ui state
    dispatch(resetSnippetUiThunk());
  };
}


/**
 * Checks for non-resolvable action collisions and either inserts the snippet or
 * returns the collisions
 */
export function checkCollisionsAndInsertSnippetThunk(args: {
  snippet: SnippetVersion;
  scope: SnippetScope;
  supportsLinkedSnippets: boolean;
}) {
  return async (
    dispatch: AppDispatch,
    getState: () => State,
  ): Promise<{
    success: boolean;
    collisions?: Action[];
  }> => {
    const { snippet, scope, supportsLinkedSnippets } = args;

    const workflowActions = selectActions(getState());
    const collisions = getSnippetActionsWithNonResolvableCollisions({
      snippetActions: snippet.data.actions,
      workflowActions: workflowActions ?? {},
    });

    if (collisions.length > 0) {
      dispatch(setSnippetMode({ mode: null }));
      return { success: false, collisions };
    }

    if (scope === 'local' && supportsLinkedSnippets) {
      await dispatch(insertLinkedSnippetVersionThunk({ snippet }));
    } else {
      await dispatch(insertSnippetVersionThunk({ snippet }));
    }
    return { success: true };
  };
}

// ==== linked snippets
export function insertLinkedSnippetVersionThunk(args: {
  snippet: SnippetVersion;
}) {
  return async (dispatch: AppDispatch) => {
    const { snippet } = args;

    try {
      // state and UI updates before running the blocking operation
      dispatch(setSnippetMode({ mode: snippetMode.pastePending }));
      dispatch(exitLinkedChildActionThunk());

      // yield control to main thread before running the blocking operation to render the spinner
      await yieldToMain();

      dispatch(preparePreInsertSnapshotThunk());

      /**
       * the snippet action id needs to be generated ahead of time so that the state for
       * its container can be created with the correct id
       */
      const parentSnippetActionPublicId = generatePublicId('action', ActionType.SNIPPET);
      dispatch(setSnippetCollapsed({
        isCollapsed: false,
        snippetId: parentSnippetActionPublicId,
      }));

      const {
        localToRemotePublicIds,
        snippet: remappedSnippet,
      } = regenerateLinkedSnippetsPublicIds({ snippet });

      // run blocking operation
      dispatch(processLinkedSnippetVersionInsertionThunk({
        snippet: remappedSnippet,
        localToRemotePublicIds,
        parentSnippetActionPublicId,
      }));

      dispatch(updateDependencies());

      // handle post merge ui changes below
      dispatch(setSnippetActiveOperation({
        operation: snippetOperation.insertLinkedSnippet,
        snippetActionPublicId: null,
      }));
      dispatch(setSnippetMode({ mode: snippetMode.review }));
    } catch (error) {
      dispatch(undoInsertSnippetVersionThunk());
      error.name = 'InsertLinkedSnippetError';
      datadogRum.addError(error, {
        operation: 'insert_linked_snippet',
        url: window.location.pathname,
      });
      captureExceptionsToSentry(error);
      throw error;
    }
  };
}

export function processLinkedSnippetVersionInsertionThunk(args: {
  snippet: SnippetVersion;
  localToRemotePublicIds: Record<PublicId, PublicId>;
  parentSnippetActionPublicId: PublicId;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const {
      snippet: unpreparedSnippet,
      localToRemotePublicIds,
      parentSnippetActionPublicId,
    } = args;


    const snippet = prepareLinkedSnippetForInsertion({
      snippet: unpreparedSnippet,
      localToRemotePublicIds,
      parentSnippetActionPublicId,
    });


    const externalWorkflowActions = selectExternalWorkflowActions(getState());
    const workflowExternalActionsCollisionMap = getCollisionsWithinPrimitives({
      primitives: Object.values(externalWorkflowActions),
    });


    const mergedActionMap = new Map<PublicId, PublicId>();

    batch(() => {
      for (const snippetAction of Object.values(snippet.data.actions)) {
        if (isExternalSnippetChildAction(snippetAction)) {
          const equivalencyKey = getEquivalencyKeyForAction({ action: snippetAction });
          const collidingActionPublicIds = workflowExternalActionsCollisionMap.get(equivalencyKey);

          if ((collidingActionPublicIds?.size ?? 0) > 0) {
            // there will be at most 1 collision on external actions, so this is safe
            const [equivalentWorkflowActionPublicId] = collidingActionPublicIds.values();

            // mark the action as merged
            mergedActionMap.set(snippetAction.publicId, equivalentWorkflowActionPublicId);

            const equivalentWorkflowAction = selectActionByPublicId(
              equivalentWorkflowActionPublicId,
            )(getState());

            const updatedAction = appendSnippetParent({
              action: equivalentWorkflowAction,
              parentSnippetActionPublicId,
              remoteActionPublicId: localToRemotePublicIds[snippetAction.publicId],
            });

            // replace the whole action (has the same public id so only contents change)
            dispatch(updateAction({
              path: equivalentWorkflowAction.publicId,
              value: updatedAction,
              skipSnapshotAndDependencies: true,
            }));

            /**
             * merge the action and move on - we use continue because we still want to run the rest of the logic
             * for external actions without a collision
             */
            /* eslint-disable-next-line no-continue */
            continue;
          }
        }


        let thresholdsToAdd: Threshold[] | undefined;
        if (actionHasRules(snippetAction)) {
          thresholdsToAdd = resolveThresholdsForAction({
            action: snippetAction,
            actionIdMap: mergedActionMap,
            thresholds: snippet.data.thresholds,
          });
        }

        let dataRoutesToAdd: DataRoute[] | undefined;
        if (actionHasDataRoutes(snippetAction)) {
          dataRoutesToAdd = resolveDataRoutesForAction({
            action: snippetAction,
            actionIdMap: mergedActionMap,
            dataRoutes: snippet.data.dataRoutes,
          });
        }

        dispatch(addAction({
          action: snippetAction,
          thresholds: thresholdsToAdd,
          dataRoutes: dataRoutesToAdd,
        }));
      }
    });
  };
}


export function upgradeLinkedSnippetVersionThunk(args: {
  localSnippetActionPublicId: PublicId;
  currentRemoteSnippet: SnippetVersion;
  nextRemoteSnippet: SnippetVersion;
}) {
  return async (dispatch: AppDispatch) => {
    // todo: BUILD-4349
    //
    try {
      // state and UI updates before running the blocking operation
      dispatch(setSnippetMode({ mode: snippetMode.pastePending }));
      dispatch(exitLinkedChildActionThunk());

      // yield control to main thread before running the blocking operation to render the spinner
      await yieldToMain();

      dispatch(preparePreInsertSnapshotThunk());

      dispatch(processLinkedSnippetVersionUpgradeThunk(args));

      dispatch(updateDependencies());

      // handle post merge ui changes below
      dispatch(setSnippetActiveOperation({
        operation: snippetOperation.upgradeLinkedSnippet,
        snippetActionPublicId: args.localSnippetActionPublicId,
      }));
      dispatch(setFocusedNodeId(args.localSnippetActionPublicId));
      dispatch(setSnippetMode({ mode: snippetMode.review }));
    } catch (error) {
      dispatch(undoInsertSnippetVersionThunk());
      error.name = 'UpgradeLinkedSnippetError';
      datadogRum.addError(error, {
        operation: 'upgrade_linked_snippet',
        url: window.location.pathname,
        // todo: finalize these
        // snippetToken: currentRemoteSnippet.metadata.token,
        // currentVersion: currentRemoteSnippet.metadata.version,
        // nextVersion: nextRemoteSnippet.metadata.version
      });
      captureExceptionsToSentry(error);
      throw error;
    }
  };
}


export function processLinkedSnippetVersionUpgradeThunk(args: {
  localSnippetActionPublicId: PublicId;
  currentRemoteSnippet: SnippetVersion;
  nextRemoteSnippet: SnippetVersion;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const {
      localSnippetActionPublicId,
      currentRemoteSnippet,
      nextRemoteSnippet,
    } = args;

    const initialState = getState();

    // since we currently only support linked snippets from the local library, we only need the graph data itself (no props)
    const currentLocalSnippetData = selectLocalSnippetData({
      snippetActionPublicId: localSnippetActionPublicId,
    })(initialState);

    const nextLocalSnippet = buildLocalNextSnippet({
      localCurrentSnippetData: currentLocalSnippetData,
      remoteCurrentSnippet: currentRemoteSnippet,
      localSnippetActionPublicId,
      remoteNextSnippet: nextRemoteSnippet,
    });

    const nextWorkflowData = applySnippetVersionUpgrade({
      localSnippetActionPublicId,
      snippet: nextLocalSnippet,
      workflow: {
        actions: selectActions(initialState),
        thresholds: selectThresholds(initialState),
        dataRoutes: selectDataRoutes(initialState),
      },
    });

    dispatch(replaceWorkflowDataGraph(nextWorkflowData));
  };
}
